import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Api } from "../lib/api";
import { Button, Card, Spinner } from "../components/ui";
import "./Plan.css";

export default function Plan() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [allPlans, monitor] = await Promise.all([Api.listPlans(), Api.getMonitor()]);
        const hasTokens =
          (monitor?.tokens?.tokens_available ?? 0) > 0 || (monitor?.tokens?.tokens_used ?? 0) > 0;
        setPlans(hasTokens ? allPlans.filter((p) => !p.is_free) : allPlans);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function selectFreePlan(plan) {
    setBusy(true);
    try {
      await Api.selectPlan(plan.plan_id);
      navigate("/app/monitor", { replace: true });
    } finally {
      setBusy(false);
    }
  }

  async function skip() {
    setBusy(true);
    try {
      await Api.skipPlan();
      navigate("/app/monitor", { replace: true });
    } finally {
      setBusy(false);
    }
  }

  function selectPlan(plan) {
    if (plan.is_free) {
      selectFreePlan(plan);
    } else {
      setUpgradeMessage(
        `To subscribe to ${plan.name}, complete checkout from your account -- billing is handled here on desktop, so this app stays free of extra processing fees.`
      );
    }
  }

  return (
    <div className="tg-plan-screen">
      <div className="tg-plan-scroll tg-scroll">
        <h1 style={{ fontSize: 24 }}>Choose your plan</h1>
        <p style={{ color: "var(--slate)", fontSize: 13.5, marginTop: 4, marginBottom: 20 }}>
          You can change this anytime from Profile → Manage plan.
        </p>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
            <Spinner size={28} />
          </div>
        ) : (
          <div className="tg-plan-grid">
            {plans.map((plan) => (
              <PlanCard key={plan.plan_id} plan={plan} loading={busy} onSelect={() => selectPlan(plan)} />
            ))}
          </div>
        )}

        {upgradeMessage && (
          <Card style={{ marginTop: 16 }}>
            <p style={{ fontSize: 13.5 }}>{upgradeMessage}</p>
          </Card>
        )}
      </div>

      <button className="tg-plan-skip" onClick={skip} disabled={busy}>
        {busy ? "…" : "Skip"}
      </button>
    </div>
  );
}

function PlanCard({ plan, loading, onSelect }) {
  return (
    <Card className="tg-plan-card">
      <h3 style={{ fontSize: 15 }}>{plan.name}</h3>
      <p className="tg-plan-card__price">{plan.rate === 0 ? "Free" : `$${plan.rate.toFixed(0)}/${plan.cycle}`}</p>
      <ul className="tg-plan-card__list">
        <li>{plan.token_allowance.toLocaleString()} tokens / {plan.cycle}</li>
        <li>{plan.days_allowance} days of access included</li>
        <li>{plan.includes_auto_alert ? "Token alerts included" : "No token alerts"}</li>
        <li>{plan.includes_auto_cutoff ? "Auto cutoff included" : "No auto cutoff"}</li>
      </ul>
      <Button fullWidth disabled={loading} onClick={onSelect}>
        {plan.is_free ? "Select" : "View upgrade options"}
      </Button>
    </Card>
  );
}
